import { notify, confirmBottom, checkedFetch } from './feedback';
import React, { useState, useEffect } from 'react';
import './FinancialReports.css';

export default function FinancialReports({ API_BASE }) {
    const [activeTab, setActiveTab] = useState('cashflow');
    const [incomes, setIncomes] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [debts, setDebts] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [incRes, expRes, debtRes] = await Promise.all([
                fetch(`${API_BASE}/v1/incomes`),
                fetch(`${API_BASE}/v1/expenses`),
                fetch(`${API_BASE}/v1/debts`)
            ]);
            const incData = await incRes.json();
            const expData = await expRes.json();
            const debtData = await debtRes.json();
            setIncomes(Array.isArray(incData) ? incData : []);
            // only approved or successful expenses count in statements
            setExpenses(Array.isArray(expData) ? expData.filter(e => e.status === 'Approved' || e.status === 'SUCCESSFUL') : []);
            setDebts(Array.isArray(debtData) ? debtData : []);
        } catch (err) {
            console.error("Failed to fetch reports data", err);
                notify("Could not load reports data.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [API_BASE]);

    return (
        <div className="financial-reports-container">
            <div className="reports-header">
                <button className={`tab-btn ${activeTab === 'cashflow' ? 'active' : ''}`} onClick={() => setActiveTab('cashflow')}>Cashflow</button>
                <button className={`tab-btn ${activeTab === 'balancesheet' ? 'active' : ''}`} onClick={() => setActiveTab('balancesheet')}>Balance Sheet</button>
                <button className={`tab-btn ${activeTab === 'debts' ? 'active' : ''}`} onClick={() => setActiveTab('debts')}>Debt Manager</button>
                <button className={`tab-btn ${activeTab === 'expenses' ? 'active' : ''}`} onClick={() => setActiveTab('expenses')}>Expenses</button>
            </div>

            <div className="reports-content">
                {loading ? <p className="loading-text">Loading financial data...</p> : (
                    <>
                        {activeTab === 'cashflow' && <CashflowStatement incomes={incomes} expenses={expenses} API_BASE={API_BASE} onRefresh={fetchData} />}
                        {activeTab === 'balancesheet' && <BalanceSheet incomes={incomes} expenses={expenses} debts={debts} />}
                        {activeTab === 'debts' && <DebtManager debts={debts} API_BASE={API_BASE} onRefresh={fetchData} />}
                        {activeTab === 'expenses' && <ExpensesBreakdown expenses={expenses} />}
                    </>
                )}
            </div>
        </div>
    );
}

function CashflowStatement({ incomes, expenses, API_BASE, onRefresh }) {
    const totalIncome = incomes.reduce((sum, item) => sum + Number(item.amount), 0);
    const totalExpense = expenses.reduce((sum, item) => sum + Number(item.amount), 0);
    const netCashflow = totalIncome - totalExpense;

    const [showIncomeForm, setShowIncomeForm] = useState(false);
    const [form, setForm] = useState({ amount: '', source: '', category: '', description: '', logged_by: 'System' });

    const handleAddIncome = async (e) => {
        e.preventDefault();
        await fetch(`${API_BASE}/v1/incomes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(form)
        });
        setShowIncomeForm(false);
        onRefresh();
    };

    return (
        <div className="report-card">
            <h2>Cashflow Statement</h2>
            <div className="summary-cards">
                <div className="card income">
                    <h3>Total Income</h3>
                    <p>₦{totalIncome.toLocaleString()}</p>
                </div>
                <div className="card expense">
                    <h3>Total Expenses</h3>
                    <p>₦{totalExpense.toLocaleString()}</p>
                </div>
                <div className={`card net ${netCashflow >= 0 ? 'positive' : 'negative'}`}>
                    <h3>Net Cashflow</h3>
                    <p>₦{netCashflow.toLocaleString()}</p>
                </div>
            </div>

            <div className="action-bar">
                <button onClick={() => setShowIncomeForm(!showIncomeForm)} className="action-btn">
                    {showIncomeForm ? 'Cancel' : '+ Record Income'}
                </button>
            </div>

            {showIncomeForm && (
                <form className="inline-form" onSubmit={handleAddIncome}>
                    <input type="number" placeholder="Amount" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required />
                    <input type="text" placeholder="Source (e.g., Client Name)" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} required />
                    <input type="text" placeholder="Category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} required />
                    <input type="text" placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                    <button type="submit" className="submit-btn">Save Income</button>
                </form>
            )}

            <h3>Recent Income Logs</h3>
            <div className="table-container" tabIndex={0} role="region" aria-label="Financial report table"><table className="data-table">
                <thead><tr><th>Date</th><th>Source</th><th>Category</th><th>Amount</th></tr></thead>
                <tbody>
                    {incomes.slice(0, 10).map(inc => (
                        <tr key={inc.id}>
                            <td>{new Date(inc.date).toLocaleDateString()}</td>
                            <td>{inc.source}</td>
                            <td>{inc.category}</td>
                            <td className="amount-positive">₦{Number(inc.amount).toLocaleString()}</td>
                        </tr>
                    ))}
                    {incomes.length === 0 && <tr><td colSpan="4">No income recorded yet.</td></tr>}
                </tbody>
            </table></div>
        </div>
    );
}

function BalanceSheet({ incomes, expenses, debts }) {
    const totalIncome = incomes.reduce((sum, item) => sum + Number(item.amount), 0);
    const totalExpense = expenses.reduce((sum, item) => sum + Number(item.amount), 0);
    const cashOnHand = totalIncome - totalExpense; // Idealized cash, normally fetched from bank

    const accountsReceivable = debts.filter(d => d.type === 'RECEIVABLE' && d.status !== 'SETTLED')
        .reduce((sum, d) => sum + Number(d.outstanding_amount), 0);

    const accountsPayable = debts.filter(d => d.type === 'PAYABLE' && d.status !== 'SETTLED')
        .reduce((sum, d) => sum + Number(d.outstanding_amount), 0);

    const totalAssets = cashOnHand + accountsReceivable;
    const totalLiabilities = accountsPayable;
    const equity = totalAssets - totalLiabilities;

    return (
        <div className="report-card">
            <h2>Balance Sheet</h2>
            <div className="balance-sheet-grid">
                <div className="sheet-column">
                    <h3>Assets (What we own)</h3>
                    <div className="sheet-row"><span>Cash (Est)</span><span>₦{cashOnHand.toLocaleString()}</span></div>
                    <div className="sheet-row"><span>Accounts Receivable</span><span>₦{accountsReceivable.toLocaleString()}</span></div>
                    <div className="sheet-row total"><span>Total Assets</span><span>₦{totalAssets.toLocaleString()}</span></div>
                </div>
                <div className="sheet-column">
                    <h3>Liabilities & Equity</h3>
                    <div className="sheet-row"><span>Accounts Payable (Debt)</span><span className="negative">₦{accountsPayable.toLocaleString()}</span></div>
                    <div className="sheet-row total"><span>Total Liabilities</span><span>₦{totalLiabilities.toLocaleString()}</span></div>

                    <h3 style={{ marginTop: '20px' }}>Equity</h3>
                    <div className="sheet-row"><span>Retained Earnings</span><span className={equity >= 0 ? 'positive' : 'negative'}>₦{equity.toLocaleString()}</span></div>
                    <div className="sheet-row total"><span>Total L & E</span><span>₦{(totalLiabilities + equity).toLocaleString()}</span></div>
                </div>
            </div>
        </div>
    );
}

    function DebtManager({ debts, API_BASE, onRefresh }) {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ type: 'RECEIVABLE', entity_name: '', principal_amount: '', due_date: '', description: '', created_by: 'System' });
    const [repayId, setRepayId] = useState(null);
    const [repayAmount, setRepayAmount] = useState('');
    const [repayComment, setRepayComment] = useState('');

    const handleAddDebt = async (e) => {
        e.preventDefault();
        try {
            await checkedFetch(`${API_BASE}/v1/debts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            setShowForm(false);
            onRefresh();
            notify('Debt record saved.', 'success');
        } catch (error) { notify(error.message || 'Could not save the change.', 'error'); }
    };

    const handleMarkSettled = async (id) => {
        if (!(await confirmBottom("Mark this entire debt as settled?"))) return;
        try {
            await checkedFetch(`${API_BASE}/v1/debts/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: 'SETTLED', outstanding_amount: 0 })
            });
            onRefresh();
            notify('Debt marked as settled.', 'success');
        } catch (error) { notify(error.message || 'Could not save the change.', 'error'); }
    };

    const handlePartialRepayment = async (debt) => {
        const amount = Number(repayAmount);
        if (!amount || amount <= 0) return notify("Please enter a valid amount");
        if (amount > debt.outstanding_amount) return notify("Repayment cannot exceed outstanding amount");

        const newOutstanding = Number(debt.outstanding_amount) - amount;
        const newStatus = newOutstanding <= 0 ? 'SETTLED' : debt.status;
        
        const timestamp = new Date().toLocaleDateString('en-GB');
        const commentNote = repayComment ? ` | ${timestamp} Repaid ₦${amount.toLocaleString()}: ${repayComment}` : ` | ${timestamp} Repaid ₦${amount.toLocaleString()}`;
        const updatedDescription = (debt.description || '') + commentNote;

        try {
            await checkedFetch(`${API_BASE}/v1/debts/${debt.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    outstanding_amount: newOutstanding,
                    status: newStatus,
                    description: updatedDescription
                })
            });

            setRepayId(null);
            setRepayAmount('');
            setRepayComment('');
            onRefresh();
            notify('Repayment recorded.', 'success');
        } catch (error) { notify(error.message || 'Could not save the change.', 'error'); }
    };

    const receivables = debts.filter(d => d.type === 'RECEIVABLE');
    const payables = debts.filter(d => d.type === 'PAYABLE');

    return (
        <div className="report-card">
            <h2>Debt Manager (Accounts Payable & Receivable)</h2>
            <button onClick={() => setShowForm(!showForm)} className="action-btn" style={{ marginBottom: '15px' }}>
                {showForm ? 'Cancel' : '+ Record New Debt / Invoice'}
            </button>

            {showForm && (
                <form className="inline-form" onSubmit={handleAddDebt}>
                    <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                        <option value="RECEIVABLE">Someone owes us (Receivable)</option>
                        <option value="PAYABLE">We owe someone (Payable)</option>
                    </select>
                    <input type="text" placeholder="Entity (Client/Vendor Name)" value={form.entity_name} onChange={e => setForm({ ...form, entity_name: e.target.value })} required />
                    <input type="number" placeholder="Amount" value={form.principal_amount} onChange={e => setForm({ ...form, principal_amount: e.target.value })} required />
                    <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
                    <input type="text" placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                    <button type="submit" className="submit-btn">Save</button>
                </form>
            )}

            <div className="debt-tables">
                <div>
                    <h3 className="positive-title">Accounts Receivable (Money coming in)</h3>
                    <div className="table-container" tabIndex={0} role="region" aria-label="Financial report table"><table className="data-table">
                        <thead><tr><th>Entity</th><th>Total Amount</th><th>Balance</th><th>Status</th><th>Action</th></tr></thead>
                        <tbody>
                            {receivables.map(d => (
                                <tr key={d.id}>
                                    <td>
                                        <div className="debt-entity">
                                            <strong>{d.entity_name}</strong>
                                            {d.description && <div className="debt-desc">{d.description}</div>}
                                        </div>
                                    </td>
                                    <td>₦{Number(d.principal_amount).toLocaleString()}</td>
                                    <td className="amount-negative">₦{Number(d.outstanding_amount).toLocaleString()}</td>
                                    <td><span className={`status-badge ${d.status.toLowerCase()}`}>{d.status}</span></td>
                                    <td>
                                        <div className="debt-actions">
                                            {d.status !== 'SETTLED' && (
                                                <>
                                                    {repayId === d.id ? (
                                                        <div className="repay-box-container">
                                                            <div className="repay-box">
                                                                <input 
                                                                    type="number" 
                                                                    placeholder="Amt" 
                                                                    value={repayAmount} 
                                                                    onChange={e => setRepayAmount(e.target.value)}
                                                                    autoFocus
                                                                />
                                                                <button onClick={() => handlePartialRepayment(d)} className="confirm-btn">✔</button>
                                                                <button onClick={() => setRepayId(null)} className="cancel-btn">✕</button>
                                                            </div>
                                                            <input 
                                                                type="text" 
                                                                placeholder="Add a comment..." 
                                                                value={repayComment} 
                                                                onChange={e => setRepayComment(e.target.value)}
                                                                className="repay-comment-input"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <button onClick={() => setRepayId(d.id)} className="repay-btn">Repay Part</button>
                                                            <button onClick={() => handleMarkSettled(d.id)} className="settle-btn">Settle All</button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {receivables.length === 0 && <tr><td colSpan="4">No receivables</td></tr>}
                        </tbody>
                    </table></div>
                </div>
                <div>
                    <h3 className="negative-title">Accounts Payable (Money going out)</h3>
                    <div className="table-container" tabIndex={0} role="region" aria-label="Financial report table"><table className="data-table">
                        <thead><tr><th>Entity</th><th>Total Amount</th><th>Balance</th><th>Status</th><th>Action</th></tr></thead>
                        <tbody>
                            {payables.map(d => (
                                <tr key={d.id}>
                                    <td>
                                        <div className="debt-entity">
                                            <strong>{d.entity_name}</strong>
                                            {d.description && <div className="debt-desc">{d.description}</div>}
                                        </div>
                                    </td>
                                    <td>₦{Number(d.principal_amount).toLocaleString()}</td>
                                    <td className="amount-negative">₦{Number(d.outstanding_amount).toLocaleString()}</td>
                                    <td><span className={`status-badge ${d.status.toLowerCase()}`}>{d.status}</span></td>
                                    <td>
                                        <div className="debt-actions">
                                            {d.status !== 'SETTLED' && (
                                                <>
                                                    {repayId === d.id ? (
                                                        <div className="repay-box-container">
                                                            <div className="repay-box">
                                                                <input 
                                                                    type="number" 
                                                                    placeholder="Amt" 
                                                                    value={repayAmount} 
                                                                    onChange={e => setRepayAmount(e.target.value)}
                                                                    autoFocus
                                                                />
                                                                <button onClick={() => handlePartialRepayment(d)} className="confirm-btn">✔</button>
                                                                <button onClick={() => setRepayId(null)} className="cancel-btn">✕</button>
                                                            </div>
                                                            <input 
                                                                type="text" 
                                                                placeholder="Add a comment..." 
                                                                value={repayComment} 
                                                                onChange={e => setRepayComment(e.target.value)}
                                                                className="repay-comment-input"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <button onClick={() => setRepayId(d.id)} className="repay-btn">Repay Part</button>
                                                            <button onClick={() => handleMarkSettled(d.id)} className="settle-btn">Settle All</button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {payables.length === 0 && <tr><td colSpan="4">No payables</td></tr>}
                        </tbody>
                    </table></div>
                </div>
            </div>
        </div>
    );
}

function ExpensesBreakdown({ expenses }) {
    const categoryMap = expenses.reduce((acc, exp) => {
        const cat = exp.expense_category || 'Uncategorized';
        acc[cat] = (acc[cat] || 0) + Number(exp.amount);
        return acc;
    }, {});

    const rows = Object.entries(categoryMap).sort((a, b) => b[1] - a[1]);
    const total = rows.reduce((sum, [, amt]) => sum + amt, 0);

    const handleCopy = () => {
        const text = rows.map(([cat, amt]) => `${cat}\t₦${amt.toLocaleString()}`).join('\n')
            + `\nTotal\t₦${total.toLocaleString()}`;
        navigator.clipboard.writeText(text);
    };

    return (
        <div className="report-card">
            <div className="expenses-breakdown-header">
                <h2>Expenses Breakdown</h2>
                <button className="action-btn" onClick={handleCopy}>Copy Table</button>
            </div>
            <div className="table-container" tabIndex={0} role="region" aria-label="Financial report table"><table className="data-table">
                <thead>
                    <tr>
                        <th>Expense Category</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(([cat, amt]) => (
                        <tr key={cat}>
                            <td>{cat}</td>
                            <td className="amount-negative">₦{amt.toLocaleString()}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr><td colSpan="2">No approved expenses found.</td></tr>
                    )}
                </tbody>
                {rows.length > 0 && (
                    <tfoot>
                        <tr className="table-total-row">
                            <td>Total</td>
                            <td>₦{total.toLocaleString()}</td>
                        </tr>
                    </tfoot>
                )}
            </table></div>
        </div>
    );
}
